// =============================================================================
//  nis365Client.ts - wrapper fetch typed pour l API NIS365
//
//  Toutes les reponses API ont la forme { data, meta } ou { error, meta }.
//  Le wrapper deballe data ou throw une AbortError contenant le code/message.
// =============================================================================

import { API_BASE_URL } from './liveMode';
import { Scan, Tenant, SubscriptionPlan } from '../domain/types';

// ----------------------------------------------------------------------------
// Types matches avec le backend (PowerShell ConvertTo-Json output)
// ----------------------------------------------------------------------------

/** Shape brute d un client renvoyee par l API (camelCase). */
interface ApiClient {
  id:               string;
  clientId:         string;
  type:             'client';
  name:             string;
  domain:           string;
  m365TenantId:     string;
  slug:             string;
  industry:         string;
  size:             number;
  subscriptionPlan: SubscriptionPlan;
  consentedAt?:     string | null;
  createdAt:        string;
  lastScanAt?:      string | null;
  lastScore?:       number | null;
  users?:           string[];
}

/** Shape brute d un scan API : compatible avec Scan du domaine (champs en PascalCase). */
interface ApiScan {
  id:           string;
  scanId:       string;
  clientId:     string;
  type:         'scan';
  status:       string;
  scanType:     'full' | 'partial';
  triggeredBy:  string;
  startedAt:    string;
  finishedAt?:  string | null;
  globalScore?: number | null;
  m365TenantId: string;
  clientSlug:   string;
  results:      Array<{
    ControlId:   string;
    Status:      string;
    Score:       number;
    Message:     string;
    EvaluatedAt: string;
    EvidenceUrl?: string | null;
  }>;
  meta: {
    catalogVersion:   string;
    subscriptionPlan: SubscriptionPlan;
  };
}

interface ApiEnvelope<T> {
  data?:  T;
  error?: { code: string; message: string; details?: unknown };
  meta:   { requestId: string; ts: string };
}

// ----------------------------------------------------------------------------
// Fetch wrapper
// ----------------------------------------------------------------------------

class ApiError extends Error {
  constructor(public code: string, message: string, public details?: unknown, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const resp = await fetch(url, {
    ...init,
    credentials: 'include',   // session cookie cross-origin (CORS + Allow-Credentials)
    headers: {
      'Content-Type': 'application/json',
      Accept:         'application/json',
      ...(init?.headers ?? {}),
    },
  });

  const text = await resp.text();
  let json: ApiEnvelope<T> | null = null;
  try {
    json = text ? JSON.parse(text) as ApiEnvelope<T> : null;
  } catch {
    throw new ApiError('parse_error', `Reponse non-JSON de l API (HTTP ${resp.status})`, text, resp.status);
  }

  if (!resp.ok || json?.error) {
    const err = json?.error;
    throw new ApiError(
      err?.code    ?? 'http_error',
      err?.message ?? `HTTP ${resp.status}`,
      err?.details,
      resp.status,
    );
  }

  return (json?.data ?? null) as T;
}

// ----------------------------------------------------------------------------
// Mappers API -> domaine front
// ----------------------------------------------------------------------------

function mapClientToTenant(c: ApiClient): Tenant {
  // v2026.06.86 : defaults safe (anciens clients en base peuvent ne pas avoir
  // size/industry alimentes -> evite TenantsPage:537 'cannot access size'.
  return {
    id:               c.clientId,
    name:             c.name             ?? '(sans nom)',
    domain:           c.domain           ?? '',
    m365TenantId:     c.m365TenantId     ?? '',
    industry:         c.industry         ?? '-',
    size:             c.size             ?? 0,
    subscriptionPlan: c.subscriptionPlan ?? 'starter',
    createdAt:        c.createdAt        ?? new Date().toISOString(),
    lastScanAt:       c.lastScanAt ?? undefined,
    lastScore:        c.lastScore  ?? undefined,
  };
}

function mapApiScanToDomain(s: ApiScan): Scan {
  return {
    id:         s.scanId,
    tenantId:   s.clientId,
    startedAt:  s.startedAt,
    finishedAt: s.finishedAt ?? s.startedAt,
    results:    s.results.map((r) => ({
      controlId:   r.ControlId,
      status:      r.Status as Scan['results'][number]['status'],
      score:       r.Score,
      message:     r.Message,
      evaluatedAt: r.EvaluatedAt,
      evidences:   r.EvidenceUrl ? [{
        capturedAt: r.EvaluatedAt,
        source:     r.EvidenceUrl,
        summary:    'Voir le blob d evidence pour le detail',
        raw:        { url: r.EvidenceUrl },
      }] : [],
    })),
    meta: {
      triggeredBy:    s.triggeredBy,
      scanType:       s.scanType,
      catalogVersion: s.meta?.catalogVersion ?? '2026.05.1',
    },
  };
}

// ----------------------------------------------------------------------------
// API publique
// ----------------------------------------------------------------------------

export const nis365Api = {
  async health(): Promise<{ status: string; service: string; version: string; env: string }> {
    return apiFetch<{ status: string; service: string; version: string; env: string }>('/health');
  },

  async listClients(): Promise<Tenant[]> {
    const raw = await apiFetch<ApiClient[]>('/clients');
    return (raw ?? []).map(mapClientToTenant);
  },

  async listScansForClient(clientId: string, limit = 20): Promise<Scan[]> {
    const raw = await apiFetch<ApiScan[]>(`/clients/${clientId}/scans?limit=${limit}`);
    return (raw ?? []).map(mapApiScanToDomain);
  },

  async getScan(scanId: string): Promise<Scan | null> {
    try {
      const raw = await apiFetch<ApiScan>(`/scans/${scanId}`);
      return raw ? mapApiScanToDomain(raw) : null;
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },

  async triggerScan(clientId: string, scanType: 'full' | 'partial' = 'full'): Promise<Scan> {
    const raw = await apiFetch<ApiScan>(
      `/clients/${clientId}/scans`,
      { method: 'POST', body: JSON.stringify({ scanType, triggeredBy: 'ui' }) },
    );
    return mapApiScanToDomain(raw);
  },
};
